import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import Imagedisplay from './Imagedisplay';
import QuestionsDisplay from './QuestionsDisplay';
import Sidebar from '../Sidebar';
import Feedback from '../Feedback';
import Footer from '../../Landing Page/Footer';
import './AnswersPage.css';

function AnswersPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { questions, answers, jobId } = location.state || {};

  const [activeIndex, setActiveIndex] = useState(-1);
  const answerRefs = useRef([]);

  // Enhanced function to create clean, readable math content without LaTeX symbols
  const preprocessMathContent = (content) => {
    if (!content) return content;
    
    let processedContent = content;
    
    // Step 1: Clean up malformed expressions and HTML remnants
    processedContent = processedContent.replace(/\$\$\s*\d+\s*\$/g, '');
    processedContent = processedContent.replace(/\$\s*\d+\s*\$/g, '');
    processedContent = processedContent.replace(/\$\$\s*\$\$/g, '');
    processedContent = processedContent.replace(/\$\s*\$/g, '');
    processedContent = processedContent.replace(/:\s*\\\s*\$\$1\$/g, ':');
    processedContent = processedContent.replace(/1\s*1/g, '');
    processedContent = processedContent.replace(/\\\s*$/gm, '');
    
    // Remove HTML tags and classes completely
    processedContent = processedContent.replace(/<[^>]*>/g, '');
    processedContent = processedContent.replace(/class\s*=\s*["'][^"']*["']/g, '');
    processedContent = processedContent.replace(/div\s*=\s*["'][^"']*["']/g, '');
    
    // Step 2: Clean LaTeX formatting
    processedContent = processedContent
      .replace(/\$\$([^$]+)\$\$/g, '$1')
      .replace(/\$([^$]+)\$/g, '$1')
      .replace(/\\(mu|tau|alpha|beta|gamma|delta|sigma|theta|pi|omega)/g, '$1')
      .replace(/\\cdot/g, '·')
      .replace(/\\times/g, '×')
      .replace(/\\[a-zA-Z]+/g, '')
      .replace(/([A-Za-z])_\{([A-Za-z0-9]+)\}/g, '$1_$2')
      .replace(/([A-Za-z])\^\{([A-Za-z0-9]+)\}/g, '$1^$2')
      .replace(/[{}]/g, '');
    
    // Step 3: Fix broken structure
    processedContent = processedContent
      // Fix broken bullet points like "Step • by • Step"
      .replace(/Step\s*•\s*by\s*•\s*Step/gi, 'Step-by-Step')
      .replace(/•\s*\n\s*•/g, ' ')
      .replace(/•\s+([a-z])/g, '• $1')
      // Clean up equations and put them on proper lines
      .replace(/\\\s*([A-Za-z_]+\s*=\s*[^\\]+)/g, '\n\n    $1')
      .replace(/\\\s*/g, '\n\n    ')
      // Fix section headers
      .replace(/([A-Z][a-zA-Z\s]*):(?!\s*\n)/g, '\n\n$1:\n')
      // Clean bullet points
      .replace(/\n\s*•\s*/g, '\n• ')
      // Fix "where" clauses
      .replace(/,\s*where\s+/g, '\n\nwhere ')
      .replace(/\)\s*,\s*where/g, ')\n\nwhere')
      // Clean parentheses
      .replace(/\(\s*([^)]+)\s*\)/g, '($1)')
      // Clean spacing
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s{2,}/g, ' ');
    
    // Step 4: Structure properly
    processedContent = processedContent
      // Add section breaks
      .replace(/(Derivation|Step-by-Step|Formula|Variables|Summary):/gi, '\n\n$1:\n')
      .replace(/Final Formula/gi, '\n\nFinal Formula:\n')
      // Fix equations - ensure they're indented
      .replace(/\n\s*([A-Za-z_]+\s*=\s*[^\n]+)/g, '\n\n    $1')
      // Clean final formatting
      .replace(/\n\n+/g, '\n\n')
      .replace(/\(\s*$/g, '')
      .replace(/\s*,\s*$/g, '')
      .trim();
    
    return processedContent;
  };

  useEffect(() => {
    // Ensure refs array length matches answers length
    answerRefs.current = (answers || []).map((_, i) => answerRefs.current[i] || React.createRef());
  }, [answers]);

  useEffect(() => {
    // Scroll the active answer into view smoothly
  if (activeIndex === -1) return;
  const ref = answerRefs.current?.[activeIndex]?.current;
  if (ref && ref.scrollIntoView) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeIndex]);

  const handleNewQuestions = () => {
    navigate('/dashboard');
  };

  return (
    <div className="ap-container">
      <Sidebar activeItem="questions" />
      
      <div className="ap-main">
        <div className="ap-header">
          <h2>Technical Interview Answers</h2>
          <p>Review the answers for your technical interview questions.</p>
        </div>

        <div className="ap-body">
          <div className="ap-grid">
            <div className="ap-left">
              {/* Image Section */}
              <div className="ap-image">
                <Imagedisplay jobId={jobId} />
              </div>

              {/* Questions Section */}
        <div className="ap-questions">
                <QuestionsDisplay 
                  questions={questions || []} 
                  showAnswers={false}
                  answers={[]}
                  loadingAnswers={false}
          error=""
          onSelect={(idx) => setActiveIndex(idx)}
                />
              </div>
            </div>

            {/* Answers Section */}
            <div className="ap-answers">
              <div className="ap-answers-display">
                <div className="ap-answers-block">
                  <h4 className="ap-section-title">Expert Answers</h4>
                  <ul className="ap-answers-list">
                    {(answers || []).map((item, index) => (
                      <li
                        key={`answer-${item.id || index}`}
                        className={`ap-answer-item ${index === activeIndex ? 'ap-answer-item--active' : ''}`}
                        ref={el => {
                          // store element in a simple array ref for scroll behavior
                          answerRefs.current[index] = { current: el };
                        }}
                      >
                        <ReactMarkdown
                          remarkPlugins={[[remarkMath, {
                            inlineMathDouble: false,
                            displayMath: [['\\[', '\\]'], ['$$', '$$']],
                            inlineMath: [['\\(', '\\)'], ['$', '$']]
                          }]]}
                          rehypePlugins={[rehypeKatex]}
                        >
                          {preprocessMathContent(item.answer || "No answer available")}
                        </ReactMarkdown>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="ap-new-btn-wrap">
            <button
              className="ap-new-btn"
              onClick={handleNewQuestions}
            >
              Generate More Questions
            </button>
          </div>
        </div>

        <Feedback />
      </div>

      <div className="dashboard-footer">
        <Footer />
      </div>
    </div>
  );
}

export default AnswersPage;
